/**
 * SoRepo sobre Supabase. Misma lógica que DexieSoRepo.
 *
 * La sub-entidad `Tarea` (PDT) se almacena como filas `entity="tarea"` en la
 * misma tabla `pmis_entities`, vinculadas por `payload.soId`. Las operaciones
 * de tareas usan el cliente Supabase directamente (no hay repo público de
 * Tarea — las gestiona la SO, igual que en Dexie).
 */

import type { SoRepo } from "../../repos/SoRepo";
import type { So, Tarea, SoHistorialEntry, VeredictoInt } from "@app-types/entities";
import {
  puedeTransicionarSo,
  cambiosConceptoInt,
  CAMPOS_CONCEPTO_INT,
} from "@/features/so/soRules";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";
import { supabase, TABLE, SupabaseRepoError } from "./supabaseClient";

export class SupabaseSoRepo extends SupabaseBaseRepo<So> implements SoRepo {
  constructor(tenantId: string) {
    super("so", tenantId);
  }

  async list(filter?: Partial<So>): Promise<So[]> {
    const all = await this.listAll();
    if (filter?.estado) return all.filter((s) => s.estado === filter.estado);
    return all;
  }



  async listActive(): Promise<So[]> {
    return (await this.listAll()).filter((s) => s.estado !== "CANCELADA" && s.estado !== "CERRADA");
  }

  async getNextSoCode(): Promise<string> {
    const all = await this.listAll();
    let max = 0;
    for (const s of all) {
      const m = /(\d+)\s*$/.exec(String(s.codigo ?? ""));
      if (m) {
        const n = parseInt(m[1] ?? "", 10);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
    return `OT-${String(max + 1).padStart(4, "0")}`;
  }

  async get(id: string): Promise<So | null> {
    return this.getById(id);
  }

  async create(data: Omit<So, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: So = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<So>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`SO ${id} not found`);
    const updated: So = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: So["estado"], userEmail: string, observaciones?: string): Promise<void> {
    const so = await this.get(id);
    if (!so) throw new Error(`SO ${id} not found`);
    if (so.estado === nextState) return;
    if (!puedeTransicionarSo(so.estado, nextState)) {
      throw new Error(`Transicion ${so.estado} -> ${nextState} no permitida`);
    }
    const entry: SoHistorialEntry = {
      fecha: new Date().toISOString(),
      estadoAnterior: so.estado,
      estadoNuevo: nextState,
      usuario: userEmail || "system",
      ...(observaciones && observaciones.trim() ? { observaciones } : {}),
    };
    const historial = [...(so.historial ?? []), entry];
    const partial: Partial<So> = { estado: nextState, historial };
    if (nextState === "CANCELADA" && observaciones && observaciones.trim()) {
      partial.motivoCancelacion = observaciones;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (nextState === "EN_REVISION" && !so.fechaRecepcionCotizacion) partial.fechaRecepcionCotizacion = today;
    if (nextState === "EN_REVISION") {
      // Revisión en PARALELO (flujo canónico pt. 3): entra directo a la cola
      // del PL2 (único aprobador); el INT deja concepto consultivo. "PL" se
      // reserva al escalamiento por límite económico (updateSubFlags).
      partial.nivelRevision = so.nivelRevision === "PL" ? "PL" : "PL2";
      // RONDA NUEVA: cada entrada a revisión limpia el concepto de
      // Interventoría de la ronda anterior. Sin esto, una OT reprogramada
      // volvería a la cola del PL2 con el "Aprobado por Interventoría" de una
      // línea base que el Interventor nunca vio. La constancia permanente vive
      // en el historial, que no se toca.
      for (const c of CAMPOS_CONCEPTO_INT) {
        (partial as Record<string, unknown>)[c] = undefined;
      }
    }
    if (nextState === "APROBADA" && !so.fechaAprobacion) partial.fechaAprobacion = today;
    if (nextState === "APROBADA") {
      partial.aprobadoPor = userEmail || "system";
      (partial as Record<string, unknown>).nivelRevision = undefined;
      partial.enReprogramacion = false;
      if (!so.fechaInicioEjecucion) partial.fechaInicioEjecucion = today;
    }
    if (nextState === "CERRADA" && !so.fechaCierre) {
      partial.fechaCierre = today;
      partial.enReprogramacion = false;
    }
    if (nextState === "EN_COTIZACION") {
      // El concepto NO se limpia aquí: si Interventoría rechazó, el contratista
      // debe seguir viendo el motivo mientras corrige. Se limpia al reenviar.
      (partial as Record<string, unknown>).nivelRevision = undefined;
    }
    await this.update(id, partial, so.version);
  }

  async updateSubFlags(
    id: string,
    flags: {
      nivelRevision?: "INT" | "PL2" | "PL" | null;
      enReprogramacion?: boolean;
      conceptoInt?: string;
      aprobadoPorInt?: string;
      veredictoInt?: VeredictoInt;
      fechaConceptoInt?: string;
      montoConceptoInt?: number;
    },
    userEmail: string,
  ): Promise<void> {
    const so = await this.get(id);
    if (!so) throw new Error(`SO ${id} not found`);
    const cambios: Partial<So> = {};
    let descripcionCambio = "";
    if (flags.nivelRevision !== undefined) {
      if (flags.nivelRevision === null) (cambios as Record<string, unknown>).nivelRevision = undefined;
      else cambios.nivelRevision = flags.nivelRevision;
      descripcionCambio = `nivel de revisión → ${flags.nivelRevision ?? "(ninguno)"}`;
    }
    // Concepto / veredicto de Interventoría: regla ÚNICA compartida por los
    // tres adaptadores (valida el estado y arma los campos sin pisar el texto
    // previo cuando se aprueba sin escribir nada).
    if (flags.conceptoInt !== undefined || flags.veredictoInt !== undefined) {
      const r = cambiosConceptoInt(so, flags, userEmail);
      Object.assign(cambios, r.cambios);
      descripcionCambio = r.nota;
    }
    if (flags.enReprogramacion !== undefined) {
      cambios.enReprogramacion = flags.enReprogramacion;
      descripcionCambio = flags.enReprogramacion ? "iniciada reprogramación de LB" : "reprogramación finalizada";
    }
    if (Object.keys(cambios).length === 0) return;
    const entry: SoHistorialEntry = {
      fecha: new Date().toISOString(),
      estadoAnterior: so.estado,
      estadoNuevo: so.estado,
      usuario: userEmail || "system",
      observaciones: descripcionCambio,
    };
    cambios.historial = [...(so.historial ?? []), entry];
    await this.update(id, cambios, so.version);
  }

  async registrarAceptacion(id: string, userEmail: string, observaciones?: string): Promise<void> {
    const so = await this.get(id);
    if (!so) throw new Error(`OT ${id} not found`);
    if (so.estado !== "APROBADA") throw new Error("Solo se puede aceptar una OT APROBADA.");
    if (so.aceptadaPorContratista) return;
    const now = new Date().toISOString();
    const entry: SoHistorialEntry = {
      fecha: now,
      estadoAnterior: so.estado,
      estadoNuevo: so.estado,
      usuario: userEmail || "system",
      observaciones: `Aceptación formal del contratista${observaciones ? `: ${observaciones}` : ""}`,
    };
    const cambios: Partial<So> = {
      aceptadaPorContratista: true,
      fechaAceptacion: now,
      usuarioAceptacion: userEmail || "system",
      ...(observaciones ? { observacionAceptacion: observaciones } : {}),
      historial: [...(so.historial ?? []), entry],
    };
    await this.update(id, cambios, so.version);
  }

  async getMontoTotal(id: string): Promise<number> {
    const tareas = await this.listTareas(id);
    return tareas.reduce((sum, t) => sum + t.cantidad * t.precioUnitario, 0);
  }

  // ── Sub-entidad Tarea (entity="tarea") ────────────────────────────

  private tareaRow(t: Tarea): Record<string, unknown> {
    return {
      entity: "tarea",
      id: t.id,
      tenant_id: this.tenantId,
      version: t.version,
      payload: t,
      created_at: t.createdAt,
      updated_at: t.updatedAt,
    };
  }

  private async allTareas(): Promise<Tarea[]> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", "tarea")
      .eq("tenant_id", this.tenantId);
    if (error) throw new SupabaseRepoError(error.message, "listTareas");
    return ((data ?? []) as { payload: Tarea }[]).map((r) => r.payload);
  }

  async listTareas(soId: string): Promise<Tarea[]> {
    const tareas = (await this.allTareas()).filter((t) => t.soId === soId);
    return tareas.sort(
      (a, b) => (a.orden ?? Number.MAX_SAFE_INTEGER) - (b.orden ?? Number.MAX_SAFE_INTEGER),
    );
  }

  async listAllTareas(): Promise<Tarea[]> {
    return this.allTareas();
  }

  async createTarea(
    soId: string,
    data: Omit<Tarea, "id" | "soId" | "createdAt" | "updatedAt" | "version">,
  ): Promise<string> {
    const now = new Date().toISOString();
    const nueva: Tarea = {
      ...data,
      id: crypto.randomUUID(),
      soId,
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    const { error } = await supabase().from(TABLE).insert(this.tareaRow(nueva));
    if (error) throw new SupabaseRepoError(error.message, "createTarea");
    return nueva.id;
  }

  async updateTarea(tareaId: string, data: Partial<Tarea>): Promise<void> {
    const current = (await this.allTareas()).find((t) => t.id === tareaId);
    if (!current) throw new Error(`Tarea ${tareaId} not found`);
    const updated: Tarea = {
      ...current,
      ...data,
      id: current.id,
      soId: current.soId,
      tenantId: current.tenantId,
      createdAt: current.createdAt,
      updatedAt: new Date().toISOString(),
      version: current.version + 1,
    };
    const { error } = await supabase()
      .from(TABLE)
      .upsert(this.tareaRow(updated), { onConflict: "entity,id" });
    if (error) throw new SupabaseRepoError(error.message, "updateTarea");
  }

  async deleteTarea(tareaId: string): Promise<void> {
    const current = (await this.allTareas()).find((t) => t.id === tareaId);
    if (!current) return;
    const so = await this.get(current.soId);
    if (!so) throw new Error(`SO ${current.soId} not found`);
    if (so.estado !== "EN_COTIZACION") {
      throw new Error(`No se puede eliminar tareas de una SO en estado ${so.estado}. Solo en EN_COTIZACION.`);
    }
    const { error } = await supabase()
      .from(TABLE)
      .delete()
      .eq("entity", "tarea")
      .eq("tenant_id", this.tenantId)
      .eq("id", tareaId);
    if (error) throw new SupabaseRepoError(error.message, "deleteTarea");
  }


  /**
   * Purga una tarea huerfana. Ver `SoRepo.purgarTareaHuerfana`: se niega si
   * la OT existe, para que este camino no sirva de atajo a la regla de
   * edicion.
   */
  async purgarTareaHuerfana(tareaId: string): Promise<void> {
    const current = (await this.allTareas()).find((t) => t.id === tareaId);
    if (!current) return;
    const so = await this.get(current.soId);
    if (so) {
      throw new Error(
        `La OT ${current.soId} SI existe: use la edicion de la cotizacion, no la purga.`,
      );
    }
    const { error } = await supabase()
      .from(TABLE)
      .delete()
      .eq("entity", "tarea")
      .eq("tenant_id", this.tenantId)
      .eq("id", tareaId);
    if (error) throw new SupabaseRepoError(error.message, "purgarTareaHuerfana");
  }

  async replaceTareas(
    soId: string,
    tareas: Omit<Tarea, "tenantId" | "createdAt" | "updatedAt" | "version">[],
  ): Promise<void> {
    const so = await this.get(soId);
    if (!so) throw new Error(`SO ${soId} not found`);
    const now = new Date().toISOString();
    const existentes = (await this.allTareas()).filter((t) => t.soId === soId);
    const idsNuevos = new Set(tareas.map((t) => t.id).filter(Boolean));
    const aEliminar = existentes.filter((t) => !idsNuevos.has(t.id)).map((t) => t.id);
    if (aEliminar.length > 0) {
      const { error } = await supabase()
        .from(TABLE)
        .delete()
        .eq("entity", "tarea")
        .eq("tenant_id", this.tenantId)
        .in("id", aEliminar);
      if (error) throw new SupabaseRepoError(error.message, "replaceTareas/delete");
    }
    const filas: Tarea[] = tareas.map((t) => {
      const previa = existentes.find((e) => e.id === t.id);
      return {
        ...t,
        id: t.id || crypto.randomUUID(),
        soId,
        tenantId: this.tenantId,
        createdAt: previa?.createdAt ?? now,
        updatedAt: now,
        version: (previa?.version ?? 0) + 1,
      };
    });
    if (filas.length > 0) {
      const { error } = await supabase()
        .from(TABLE)
        .upsert(filas.map((t) => this.tareaRow(t)), { onConflict: "entity,id" });
      if (error) throw new SupabaseRepoError(error.message, "replaceTareas/upsert");
    }
  }
}
